import React from "react";
import { useCurrentMatch } from "../hooks/useCurrentMatch";
import { useSelector } from "react-redux";
import {
  selectCurrentMatchGameOutcomes,
  selectCurrentMatchGames,
  selectCurrentMatchPlayers,
} from "../store/selectors";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { Button, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { orderBy, sumBy } from "lodash";
import { BIDS, RUFER_OPTIONS } from "../constants";

export const MatchScoreBoard: React.FC = () => {
  const { match, openGame } = useCurrentMatch();
  const games = useSelector(selectCurrentMatchGames);
  const outcomes = useSelector(selectCurrentMatchGameOutcomes);
  const matchPlayers = useSelector(selectCurrentMatchPlayers);
  const { setViewingScores, setViewingGameDetailsId, setCurrentGameId } = useCurrentUser();

  const handleClose = () => {
    setViewingScores(false);
  };

  const handleRowClick = (gameId: string) => {
    setViewingGameDetailsId(gameId);
  };

  const handleNextHand = () => {
    if (openGame) {
      setCurrentGameId(openGame.id);
      setViewingScores(false);
    }
  };

  if (
    games === undefined ||
    match === undefined ||
    matchPlayers === undefined ||
    outcomes === undefined
  ) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-950 flex items-center justify-center">
        <div className="text-emerald-200 animate-pulse">Loading scores...</div>
      </div>
    );
  }

  // Sort players by seat position for consistent ordering
  const sortedPlayers = orderBy(matchPlayers, ["seat"]);
  const sortedOutcomes = orderBy(outcomes, ["gameIndex"]);
  
  // Filter to only completed games for counting (games where players have valid scores)
  const completedOutcomes = sortedOutcomes.filter(o => 
    o.players.some(p => typeof p.score === 'number' && !isNaN(p.score))
  );

  // Calculate totals for each player (only from completed games)
  const playerTotals: Record<string, number> = {};
  sortedPlayers.forEach((p) => {
    const playerScores = completedOutcomes
      .flatMap((o) => o.players)
      .filter((op) => op.id === p.id)
      .map((op) => op.score)
      .filter((s): s is number => typeof s === 'number' && !isNaN(s));
    playerTotals[p.id] = sumBy(playerScores, (s) => s);
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-950 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {match.name}
            </h1>
            <p className="text-emerald-300 mt-1">
              {completedOutcomes.length} hand{completedOutcomes.length !== 1 ? 's' : ''} completed
            </p>
          </div>
          <IconButton
            onClick={handleClose}
            sx={{ color: "white", bgcolor: "rgba(255,255,255,0.1)" }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* Score Table */}
        <div className="bg-white/95 rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-emerald-800 text-white">
                  <th className="px-4 py-3 text-left font-semibold sticky left-0 bg-emerald-800 z-10">
                    Hand
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">Contract</th>
                  {sortedPlayers.map((player) => (
                    <th
                      key={player.id}
                      className="px-4 py-3 text-center font-semibold min-w-[100px]"
                    >
                      {player.name}
                    </th>
                  ))}
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {sortedOutcomes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3 + sortedPlayers.length}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No hands completed yet
                    </td>
                  </tr>
                ) : (
                  sortedOutcomes.map((outcome, idx) => {
                    const bidDetails =
                      BIDS.find((b) => b.slug === outcome.bid.slug) ??
                      RUFER_OPTIONS.find((b) => b.slug === outcome.bid.slug);
                    const bidName = bidDetails?.name ?? outcome.bid.slug;
                    const isComplete = outcome.players.some(
                      (p) => typeof p.score === "number" && !isNaN(p.score)
                    );

                    return (
                      <tr
                        key={outcome.id}
                        onClick={() => isComplete && handleRowClick(outcome.id)}
                        className={`border-b border-gray-200 ${
                          idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                        } hover:bg-emerald-50 transition-colors ${
                          isComplete ? "cursor-pointer" : ""
                        }`}
                      >
                        <td className="px-4 py-3 font-medium text-gray-700 sticky left-0 bg-inherit z-10">
                          {outcome.gameIndex + 1}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                            {bidName}
                          </span>
                        </td>
                        {sortedPlayers.map((player) => {
                          const playerOutcome = outcome.players.find(
                            (p) => p.id === player.id
                          );
                          const score = playerOutcome?.score;
                          const hasScore = typeof score === 'number' && !isNaN(score);
                          const isDeclarer = playerOutcome?.isDeclarer;
                          const isPartner = playerOutcome?.isPartner;
                          const isForehand = playerOutcome?.gamePosition === 0;

                          return (
                            <td
                              key={player.id}
                              className="px-4 py-3 text-center"
                            >
                              <div className="inline-flex items-center gap-1">
                                {hasScore ? (
                                  <span
                                    className={`inline-flex items-center justify-center min-w-[3rem] px-2 py-1 rounded font-semibold ${
                                      score > 0
                                        ? "text-emerald-700 bg-emerald-100"
                                        : score < 0
                                        ? "text-red-700 bg-red-100"
                                        : "text-gray-600 bg-gray-100"
                                    } ${isDeclarer ? "ring-2 ring-amber-400" : ""}`}
                                  >
                                    {score > 0 ? "+" : ""}
                                    {score}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center min-w-[3rem] px-2 py-1 rounded font-semibold text-gray-400 bg-gray-50">
                                    —
                                  </span>
                                )}
                                {isForehand && (
                                  <Tooltip title="Forehand" arrow>
                                    <span className="w-5 h-5 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center cursor-help">
                                      F
                                    </span>
                                  </Tooltip>
                                )}
                                {isPartner && (
                                  <Tooltip title="Partner" arrow>
                                    <span className="w-5 h-5 rounded-full bg-purple-500 text-white text-xs font-bold flex items-center justify-center cursor-help">
                                      P
                                    </span>
                                  </Tooltip>
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 text-center">
                          {isComplete && (
                            <ChevronRightIcon className="text-gray-400" />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}

                {/* Totals Row */}
                {sortedOutcomes.length > 0 && (
                  <tr className="bg-emerald-800 text-white font-bold">
                    <td className="px-4 py-4 sticky left-0 bg-emerald-800 z-10">
                      Total
                    </td>
                    <td className="px-4 py-4"></td>
                    {sortedPlayers.map((player) => {
                      const total = playerTotals[player.id] ?? 0;
                      return (
                        <td key={player.id} className="px-4 py-4 text-center">
                          <span
                            className={`inline-flex items-center justify-center min-w-[4rem] px-3 py-1 rounded-lg text-lg ${
                              total > 0
                                ? "bg-emerald-400 text-emerald-900"
                                : total < 0
                                ? "bg-red-400 text-red-900"
                                : "bg-gray-300 text-gray-700"
                            }`}
                          >
                            {total > 0 ? "+" : ""}
                            {total}
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-4 py-4"></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-center mt-8">
          {openGame ? (
            <Button
              onClick={handleNextHand}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#059669",
                "&:hover": { backgroundColor: "#047857" },
                px: 6,
                py: 2,
                fontSize: "1.1rem",
              }}
            >
              Next Hand
            </Button>
          ) : (
            <Button
              onClick={handleClose}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#059669",
                "&:hover": { backgroundColor: "#047857" },
                px: 6,
                py: 2,
                fontSize: "1.1rem",
              }}
            >
              Back to Game
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
